(function($) {
/**
 * Factory Manager for predefined UIControls
 *  - you can manage all your UI control logic within your shader implementation
 *  and not to touch this class at all, but here you will find some most common
 *  or some advanced controls ready to use, simple and powerful
 *  - registering an IComponent implementation (or an UiElement) in the factory results in its support
 *  among all the shaders (given the GLSL type, result of sample(...) matches).
 *  - UiElements are objects to create simple controls quickly and get rid of code duplicity,
 *  for more info @see OpenSeadragon.FlexRenderer.UIControls.register()
 * @class OpenSeadragon.FlexRenderer.UIControls
 */
$.FlexRenderer.UIControls = class {
    /**
     * Get all available control types
     * @return {string[]} array of available control types
     */
    static types() {
        return Object.keys(this._items).concat(Object.keys(this._impls));
    }

    /**
     * Get an element used to create simple controls, if you want
     * an implementation of the controls themselves (IControl), use build(...) to instantiate
     * @param {string} id type of the control
     * @return {*}
     */
    static getUiElement(id) {
        let ctrl = this._items[id];
        if (!ctrl) {
            console.error("Invalid control: " + id);
            ctrl = this._items["number"];
        }
        return ctrl;
    }

    /**
     * Get an element used to create advanced controls, if you want
     * an implementation of simple controls, use build(...) to instantiate
     * @param {string} id type of the control
     * @return {OpenSeadragon.FlexRenderer.UIControls.IControl}
     */
    static getUiClass(id) {
        let ctrl = this._impls[id];
        if (!ctrl) {
            console.error("Invalid control: " + id);
            ctrl = this._impls["colormap"];
        }
        return ctrl;
    }

    /**
     * Build UI control object based on given parameters
     * @param {OpenSeadragon.FlexRenderer.ShaderLayer} owner owner of the control, shaderLayer
     * @param {string} controlName name used for the control (eg.: opacity)
     * @param {object} controlObject object from shaderLayer.defaultControls, defines control
     * @param {string} controlId
     * @param {object|*} customParams parameters passed to the control (defined by the control) or set as default value if not object ({})
     * @return {OpenSeadragon.FlexRenderer.UIControls.IControl}
     */
    static build(owner, controlName, controlObject, controlId, customParams = {}) {
        let defaultParams = controlObject.default,
            accepts = controlObject.accepts,
            requiredParams = controlObject.required === undefined ? {} : controlObject.required;

        let interactivityEnabled = owner._interactive;

        // if not an object, but a value, make it the default one
        if (!(typeof customParams === 'object')) {
            customParams = {default: customParams};
        }
        //must be false if HTML nodes are not managed
        if (!interactivityEnabled) {
            customParams.interactive = false;
        }

        let originalType = defaultParams.type;

        // merge dP < cP < rP recursively with rP having the biggest overwriting priority, without modifying the original objects.
        // When the user picks a different `type`, defaultParams is type-specific config from the layer that
        // describes the original control type — its keys (e.g. a string `default` palette name, mode-specific
        // hints, titles) are not transferable to a different control type and would corrupt the new control's
        // expected param shape. Drop defaultParams in that case and let the chosen control's own `supports`
        // fill in defaults via getParams(). requiredParams is layer-enforced and stays.
        const userType = customParams && customParams.type;
        const typeOverridden = userType && originalType && userType !== originalType;
        const params = typeOverridden
            ? $.extend(true, {}, customParams, requiredParams)
            : $.extend(true, {}, defaultParams, customParams, requiredParams);

        if (!this._items[params.type]) {
            const controlType = params.type;

            // if cannot use the new control type, try to use the default one
            if (!this._impls[controlType]) {
                return this._buildFallback(controlType, originalType, owner, controlName, controlObject, controlId, params);
            }

            let cls = new this._impls[controlType](owner, controlName, controlId, params);

            if (accepts(cls.type, cls)) {
                return cls;
            }

            // cannot built with custom implementation, try to build with a default one
            return this._buildFallback(controlType, originalType, owner, controlName, controlObject, controlId, params);

        } else { // control's type (eg.: range/number/...) is defined in this._items
            let intristicComponent = this.getUiElement(params.type);
            let comp = new $.FlexRenderer.UIControls.SimpleUIControl(
                owner, controlName, controlId, params, intristicComponent
            );

            if (accepts(comp.type, comp)) {
                return comp;
            }
            return this._buildFallback(intristicComponent.glType, originalType,
                owner, controlName, controlObject, controlId, params);
        }
    }

    static _buildFallback(newType, originalType, owner, controlName, controlObject, controlId, customParams) {
        //repeated check when building object from type

        customParams.interactive = false;
        if (originalType === newType) { //if default and new equal, fail - recursion will not help
            console.error(`Invalid parameter in shader '${customParams.type}': the parameter could not be built.`);
            return undefined;
        } else { //otherwise try to build with originalType (default)
            customParams.type = originalType;
            console.warn("Incompatible UI control type '" + newType + "': making the input non-interactive.");
            return this.build(owner, controlName, controlObject, controlId, customParams);
        }
    }

    /**
     * Register simple UI element by providing necessary object
     * implementation:
     *  { defaults: function() {...}, // object with all default values for all supported parameters
         html: function(uniqueId, params, classes="", css="") {...}, //how the HTML UI controls look like
        glUniformFunName: function() {...}, //what function webGL uses to pass this attribute to GPU
        decode: function(fromValue) {...}, //parse value obtained from HTML controls into something
                                                gl[glUniformFunName()](...) can pass to GPU
        glType: //what's the type of this parameter wrt. GLSL: int? vec3?
        docs: object|function // optional machine-readable docs descriptor
        applyToNode: function(node, encodedValue) {...} //optional; how to write the encoded value back
                                                to the DOM element. Defaults to 'node.value = encodedValue',
                                                which is wrong for inputs whose state lives elsewhere
                                                (checkbox 'checked'). Not part of the required contract.
     * @param type the identifier under which is this control used: lookup made against params.type
     * @param uiElement the object to register, fulfilling the above-described contract
     */
    static register(type, uiElement) {
        function check(el, prop, desc) {
            if (!el[prop]) {
                console.warn(`Skipping UI control '${type}' due to '${prop}': missing ${desc}.`);
                return false;
            }
            return true;
        }

        if (check(uiElement, "defaults", "defaults():object") &&
            check(uiElement, "html", "html(uniqueId, params, css):htmlString") &&
            check(uiElement, "glUniformFunName", "glUniformFunName():string") &&
            check(uiElement, "decode", "decode(encodedValue):<compatible with glType>") &&
            check(uiElement, "normalize", "normalize(value, params):<typeof value>") &&
            check(uiElement, "sample", "sample(value, valueGlType):glslString") &&
            check(uiElement, "glType", "glType:string")
        ) {
            uiElement.prototype.getName = () => type;
            if (this._items[type]) {
                console.warn("Registering an already existing control component: ", type);
            }
            uiElement["type"] = type;
            this._items[type] = uiElement;
        }
    }

    /**
     * Register class as a UI control
     * @param {string} type unique control name / identifier
     *  The class may optionally expose machine-readable docs as either static docs() or static docs = {...}.
     * @param {OpenSeadragon.FlexRenderer.UIControls.IControl} cls to register, implementation class of the controls
     */
    static registerClass(type, cls) {
        //todo not really possible with syntax checker :/
        // if ($.FlexRenderer.UIControls.IControl.isPrototypeOf(cls)) {
        cls.prototype.getName = () => type;

        if (this._items[type]) {
            console.warn("Registering an already existing control component: ", type);
        }
        cls._type = type;
        this._impls[type] = cls;
        // } else {
        //     console.warn(`Skipping UI control '${type}': does not inherit from $.FlexRenderer.UIControls.IControl.`);
        // }
    }

    static joinClasses(...values) {
        return values.filter(value => typeof value === "string" && value.trim()).join(" ").trim();
    }

    static styleAttr(css = "") {
        return css && String(css).trim() ? ` style="${css}"` : "";
    }

    static renderTitle(title, type) {
        if (!title) {
            return "";
        }
        return `<span class="er-control__title er-control__title--${type}">${title}</span>`;
    }

    static renderControl(type, title, bodyHtml, classes = "", columns = undefined, extraAttrs = "") {
        const resolvedColumns = Number(columns) || (title ? 2 : 1);
        return `<div class="${this.joinClasses("er-control", `er-control--${type}`, classes)}" data-columns="${resolvedColumns}" ${extraAttrs}>${this.renderTitle(title, type)}<div class="er-control__body
  er-control__body--${type}">${bodyHtml}</div></div>`;
    }

    static renderInput(inputTag, type, uniqueId, attrs = "", css = "") {
        return `<${inputTag} id="${uniqueId}" class="er-control__input er-control__input--${type}"${attrs}${this.styleAttr(css)}>`;
    }
};

// Definitions of possible controls' types, simple functionalities:
$.FlexRenderer.UIControls._items = {
    number: {
        defaults: function() {
            return {title: "Number", interactive: true, default: 0, min: 0, max: 100, step: 1};
        },
        // returns string corresponding to html code for injection
        html: function(uniqueId, params, classes = "", css = "") {
            const input = `<input class="er-control__input er-control__input--number" min="${params.min}" max="${params.max}"
step="${params.step}" type="number" id="${uniqueId}"${$.FlexRenderer.UIControls.styleAttr(css)}>`;
            return $.FlexRenderer.UIControls.renderControl("number", params.title, input, classes, params.title ? 2 : 1);
        },
        glUniformFunName: function() {
            return "uniform1f";
        },
        decode: function(fromValue) {
            return Number.parseFloat(fromValue);
        },
        normalize: function(value, params) {
            return (value - params.min) / (params.max - params.min);
        },
        sample: function(name, ratio) {
            return name;
        },
        glType: "float",
        type: "number",
        docs: {
            summary: "Numeric float input control.",
            description: "Renders an HTML number input, decodes to float, normalizes values into the configured min/max range, and exposes a float GLSL uniform.",
            kind: "ui-control",
            parameters: [
                { name: "default", type: "number" },
                { name: "min", type: "number" },
                { name: "max", type: "number" },
                { name: "step", type: "number" },
                { name: "interactive", type: "boolean" },
                { name: "title", type: "string" }
            ],
            glType: "float"
        }
    },

    range: {
        defaults: function() {
            return {title: "Range", interactive: true, default: 0, min: 0, max: 100, step: 1};
        },
        html: function(uniqueId, params, classes = "", css = "") {
            const input = `<input type="range" class="er-control__input er-control__input--range" min="${params.min}" max="${params.max}" step="${params.step}" id="${uniqueId}"${$.FlexRenderer.UIControls.styleAttr(css)}>`;
            return $.FlexRenderer.UIControls.renderControl("range", params.title, input, classes, params.title ? 2 : 1);
        },
        glUniformFunName: function() {
            return "uniform1f";
        },
        decode: function(fromValue) {
            return Number.parseFloat(fromValue);
        },
        normalize: function(value, params) {
            return (value - params.min) / (params.max - params.min);
        },
        sample: function(name, ratio) {
            return name;
        },
        glType: "float",
        type: "range",
        docs: {
            summary: "Slider control for float uniforms.",
            description: "Renders an HTML range input, decodes to float, normalizes values into the configured min/max range, and exposes a float GLSL uniform.",
            kind: "ui-control",
            parameters: [
                { name: "default", type: "number" },
                { name: "min", type: "number" },
                { name: "max", type: "number" },
                { name: "step", type: "number" },
                { name: "interactive", type: "boolean" },
                { name: "title", type: "string" }
            ],
            glType: "float"
        }
    },

    color: {
        defaults: function() {
            return { title: "Color", interactive: true, default: "#fff900" };
        },
        html: function(uniqueId, params, classes = "", css = "") {
            const input = `<input type="color" id="${uniqueId}" class="er-control__input er-control__input--color"${$.FlexRenderer.UIControls.styleAttr(css)}>`;
            return $.FlexRenderer.UIControls.renderControl("color", params.title, input, classes, params.title ? 2 : 1);
        },
        glUniformFunName: function() {
            return "uniform3fv";
        },
        decode: function(fromValue) {
            try {
                let index = fromValue.startsWith("#") ? 1 : 0;
                return [
                    parseInt(fromValue.slice(index, index + 2), 16) / 255,
                    parseInt(fromValue.slice(index + 2, index + 4), 16) / 255,
                    parseInt(fromValue.slice(index + 4, index + 6), 16) / 255
                ];
            } catch (e) {
                return [0, 0, 0];
            }
        },
        normalize: function(value, params) {
            return value;
        },
        sample: function(name, ratio) {
            return name;
        },
        glType: "vec3",
        type: "color",
        docs: {
            summary: "RGB color picker control.",
            description: "Renders an HTML color input, decodes a hex color string into three normalized float components, and exposes a vec3 GLSL uniform.",
            kind: "ui-control",
            parameters: [
                { name: "default", type: "string" },
                { name: "interactive", type: "boolean" },
                { name: "title", type: "string" }
            ],
            glType: "vec3"
        }
    },

    bool: {
        defaults: function() {
            return { title: "Checkbox", interactive: true, default: true };
        },
        html: function(uniqueId, params, classes = "", css = "") {
            let value = this.decode(params.default) ? "checked" : "";
            //note a bit dirty, but works :) - we want uniform access to 'value' property of all inputs
            const input = `<input type="checkbox" id="${uniqueId}" ${value}
class="er-control__input er-control__input--bool" onchange="this.value=this.checked; return true;"${$.FlexRenderer.UIControls.styleAttr(css)}>`;
            return $.FlexRenderer.UIControls.renderControl("bool", params.title, input, classes, params.title ? 2 : 1);
        },
        glUniformFunName: function() {
            return "uniform1i";
        },
        decode: function(fromValue) {
            return fromValue && fromValue !== "false" ? 1 : 0;
        },
        // A checkbox' user-visible state is 'checked', not 'value'; the html() hack above only
        // mirrors it into 'value' on user-driven change events, so a programmatic write must
        // set both or the DOM and the control drift apart.
        applyToNode: function(node, encodedValue) {
            node.checked = !!this.decode(encodedValue);
            node.value = node.checked;
        },
        normalize: function(value, params) {
            return value;
        },
        sample: function(name, ratio) {
            return name;
        },
        glType: "bool",
        type: "bool",
        docs: {
            summary: "Boolean toggle control.",
            description: "Renders an HTML checkbox, decodes the checked state into 0 or 1, and exposes a bool-compatible GLSL uniform.",
            kind: "ui-control",
            parameters: [
                { name: "default", type: "boolean" },
                { name: "interactive", type: "boolean" },
                { name: "title", type: "string" }
            ],
            glType: "bool"
        }
    },

    select: {
        defaults: function() {
            return {
                title: "Select",
                interactive: true,
                default: 1,
                // [{ value: 0, label: "Binary" }, ...] or {0:"Binary",1:"Binary inv"}
                options: [{ value: 0, label: "Off" }, { value: 1, label: "On" }]
            };
        },
        html: function(uniqueId, params, classes = "", css = "") {
            let options = [];
            if (Array.isArray(params.options)) {
                options = params.options.map(opt => {
                    if (typeof opt === "object") {
                        return {
                            value: Number.parseInt(opt.value, 10),
                            label: opt.label || String(opt.value)
                        };
                    }
                    const v = Number.parseInt(opt, 10);
                    return { value: v, label: String(opt) };
                });
            } else if (params.options && typeof params.options === "object") {
                options = Object.entries(params.options).map(([value, label]) => ({
                    value: Number.parseInt(value, 10),
                    label: String(label)
                }));
            }

            const optionHtml = options.map(opt => {
                const selected = Number.parseInt(params.default, 10) === opt.value ? " selected" : "";
                return `<option value="${opt.value}"${selected}>${opt.label}</option>`;
            }).join("");

            const input = `<select id="${uniqueId}" class="er-control__input er-control__input--select"${$.FlexRenderer.UIControls.styleAttr(css)}>${optionHtml}</select>`;
            return $.FlexRenderer.UIControls.renderControl("select", params.title, input, classes, params.title ? 2 : 1);
        },
        glUniformFunName: function() {
            return "uniform1i";
        },
        decode: function(fromValue) {
            const parsed = Number.parseInt(fromValue, 10);
            return Number.isNaN(parsed) ? 0 : parsed;
        },
        // <option value> is always a string: assigning a number silently fails to select anything.
        applyToNode: function(node, encodedValue) {
            node.value = String(this.decode(encodedValue));
        },
        normalize: function(value, params) {
            return value;
        },
        sample: function(name, ratio) {
            return name;
        },
        glType: "int",
        type: "select_int",
        docs: {
            summary: "Integer select control.",
            description: "Renders an HTML select element, decodes the selected option value into an integer, and exposes an int GLSL uniform.",
            kind: "ui-control",
            parameters: [
                { name: "default", type: "number" },
                { name: "options", type: "array|object" },
                { name: "interactive", type: "boolean" },
                { name: "title", type: "string" }
            ],
            glType: "int"
        }
    }
};

// Implementation of UI control classes, complex functionalities.
$.FlexRenderer.UIControls._impls = {
    // e.g.: colormap: $.FlexRenderer.UIControls.ColorMap
};

/**
 * @interface
 */
$.FlexRenderer.UIControls.IControl = class IControl {

    /**
     * Optional machine-readable documentation descriptor.
     * External control registrations can override this as either:
     *  - static docs() { return {...}; }
     *  - static docs = {...}
     * @returns {object|null}
     */
    static docs() {
        return null;
    }

    /**
     * Sets common properties needed to create the controls:
     *  this.owner @extends FlexRenderer.ShaderLayer - owner
     *  this.name - name of the parameter for this.owner.[load/store]Property(...) call
     *  this.id - unique ID for HTML id attribute, to be able to locate controls in DOM,
     *      created as ${uniq}${name}-${owner.uid}
     *  this.webGLVariableName - unique webgl uniform variable name, to not to cause conflicts
     *
     * If extended (class-based definition, see registerCass) children should define constructor as
     *
     * @example
     *   constructor(owner, name, webGLVariableName, params) {
     *       super(owner, name, webGLVariableName);
     *       ...
     *       //possibly make use of params:
     *       this.params = this.getParams(params);
     *
     *       //now access params:
     *       this.params...
     *   }
     *
     * @param {ShaderLayer} owner shader context owning this control
     * @param {string} name name of the control (key to the params in the shader configuration)
     * @param {string} uniq another element to construct the DOM id from, mostly for compound controls
     */
    constructor(owner, name, id) {
        this.owner = owner;
        this.name = name;
        this.id = id;
        this.webGLVariableName = `${name}_${owner.uid}`;
        this._params = {};
        this.__onchange = {};
    }

    /**
     * Report a control whose HTML mount is not present in the DOM.
     *
     * Control init() runs inside FlexRenderer's per-shader try/catch, which reduces any throw to a
     * generic "the shader control will not work" and drops the reason. A missing mount is a host
     * integration problem (markup not inserted, or inserted after init), so it is reported here
     * where the control identity is still known and the caller skips the interactive wiring.
     *
     * Silent when the renderer has no htmlHandler: such a configuration renders without control
     * markup on purpose (navigator drawer, standalone/offscreen rendering), so an absent node is
     * expected rather than a fault.
     *
     * @param {string} className control class name used in the message
     * @param {string} [detail] what the absent node costs
     */
    _warnMissingNode(className, detail = "Cannot set event listener for the control.") {
        const renderer = this.owner && this.owner._renderer;
        if (!renderer || !renderer.htmlHandler) {
            return;
        }
        console.warn(`$.FlexRenderer.UIControls.${className}::init: HTML element with id =`,
            this.id, "not found!", detail);
    }

    /**
     * Safely sets outer params with extension from 'supports'
     *  - overrides 'supports' values with the correct type (derived from supports or supportsAll)
     *  - sets 'supports' as defaults if not set
     * @param params
     */
    getParams(params) {
        const t = this.constructor.getVarType;
        function mergeSafeType(mask, from, possibleTypes) {
            const to = Object.assign({}, mask);
            Object.keys(from).forEach(key => {
                const tVal = to[key],
                    fVal = from[key],
                    tType = t(tVal),
                    fType = t(fVal);

                const typeList = possibleTypes ? possibleTypes[key] : undefined,
                    pTypeList = typeList ? typeList.map(x => t(x)) : [];

                //our type detector distinguishes arrays and objects
                if (tVal && fVal && tType === "object" && fType === "object") {
                    to[key] = mergeSafeType(tVal, fVal, typeList);
                } else if (tVal === undefined || tType === fType || pTypeList.includes(fType)) {
                    to[key] = fVal;
                } else if (fType === "string") {
                    //try parsing NOTE: parsing from supportsAll is ignored!
                    if (tType === "number") {
                        const parsed = Number.parseFloat(fVal);
                        if (!Number.isNaN(parsed)) {
                            to[key] = parsed;
                        }
                    } else if (tType === "boolean") {
                        const value = fVal.toLowerCase();
                        if (value === "false") {
                            to[key] = false;
                        }
                        if (value === "true") {
                            to[key] = true;
                        }
                    }
                }
            });
            return to;
        }

        return mergeSafeType(this.supports, params, this.supportsAll);
    }

    /**
     * Safely check certain param value
     * @param value  value to check
     * @param defaultValue default value to return if check fails
     * @param paramName name of the param to check value type against
     * @return {boolean|number|*}
     */
    getSafeParam(value, defaultValue, paramName) {
        const t = this.constructor.getVarType;
        function nest(suppNode, suppAllNode) {
            if (t(suppNode) !== "object") {
                return [suppNode, suppAllNode];
            }
            if (!suppNode[paramName]) {
                return [undefined, undefined];
            }
            return nest(suppNode[paramName], suppAllNode ? suppAllNode[paramName] : undefined);
        }
        const param = nest(this.supports, this.supportsAll),
            tParam = t(param[0]);

        if (tParam === "object") {
            console.warn("Parameters should not be stored at object level. No type inspection is done.");
            return true; //no supported inspection
        }
        const tValue = t(value);
        //supported type OR supports all types includes the type
        if (tValue === tParam || (param[1] && param[1].map(t).includes(tValue))) {
            return value;
        }

        if (tValue === "string") {
            //try parsing NOTE: parsing from supportsAll is ignored!
            if (tParam === "number") {
                const parsed = Number.parseFloat(value);
                if (!Number.isNaN(parsed)) {
                    return parsed;
                }
            } else if (tParam === "boolean") {
                const val = value.toLowerCase();
                if (val === "false") {
                    return false;
                }
                if (val === "true") {
                    return true;
                }
            }
        }

        return defaultValue;
    }

    /**
     * Uniform behaviour wrt type checking in shaders
     * @param x
     * @return {string}
     */
    static getVarType(x) {
        if (x === undefined) {
            return "undefined";
        }
        if (x === null) {
            return "null";
        }
        return Array.isArray(x) ? "array" : typeof x;
    }

    /**
     * JavaScript initialization
     *  - read/store default properties here using this.owner.[load/store]Property(...)
     *  - work with own HTML elements already attached to the DOM
     *      - set change listeners, input values!
     */
    init() {
        throw "FlexRenderer.UIControls.IControl::init() must be implemented.";
    }

    /**
     * TODO: improve overall setter API
     * Allows to set the control value programatically.
     * Does not trigger canvas re-rednreing, must be done manually (e.g. control.owner.invalidate()).
     * @param encodedValue any value the given control can support, encoded
     *  (e.g. as the control acts on the GUI - for input number of
     *    values between 5 and 42, the value can be '6' or 6 or 6.15
     */
    set(encodedValue) {
        throw "FlexRenderer.UIControls.IControl::set() must be implemented.";
    }

    /**
     * Called when an image is rendered
     * @param {WebGLProgram} program
     * @param {WebGLRenderingContext|WebGL2RenderingContext} gl
     */
    glDrawing(program, gl) {
        //the control should send something to GPU
        throw "FlexRenderer.UIControls.IControl::glDrawing() must be implemented.";
    }

    /**
     * Called when associated webgl program is switched to
     * @param {WebGLProgram} program
     * @param {WebGLRenderingContext|WebGL2RenderingContext} gl
     */
    glLoaded(program, gl) {
        //the control should send something to GPU
        throw "FlexRenderer.UIControls.IControl::glLoaded() must be implemented.";
    }

    /**
     * Get the UI HTML controls
     *  - these can be referenced in this.init(...)
     *  - should respect this.params.interactive attribute and return non-interactive output if interactive=false
     *      - don't forget to no to work with DOM elements in init(...) in this case
     *
     * todo: when overrided value before 'init' call on params, toHtml was already called, changes might not get propagated
     *  - either: delay toHtml to trigger insertion later (not nice)
     *  - do not allow changes before init call, these changes must happen at constructor
     */
    toHtml(classes = "", css = "") {
        throw "FlexRenderer.UIControls.IControl::toHtml() must be implemented.";
    }

    /**
     * Get how many columns UI requires. Usually 2 if title showing.
     * The general design is that UI control should be a ROW element,
     * and show:
     *  - 1 column if content control shown only
     *  - 2 columns if title shown, and content control shown
     *  - 3 columns if title shown, and two content elements shown side-by-side (two methods of controlling the parameter synchronized)
     * @return {number}
     */
    get layoutColumns() {
        return this.params && this.params.title ? 2 : 1;
    }

    /**
     * Handles how the variable is being defined in GLSL
     *  - should use variable names derived from this.webGLVariableName
     */
    define() {
        throw "FlexRenderer.UIControls.IControl::define() must be implemented.";
    }

    /**
     * Sample the parameter using ratio as interpolation, must be one-liner expression so that GLSL code can write
     *    `vec3 mySampledValue = ${this.color.sample("0.2")};`
     * NOTE: you can define your own global-scope functions to keep one-lined sampling,
     * see this.owner.includeGlobalCode(...)
     * @param {(string|undefined)} value openGL value/variable, used in a way that depends on the UI control currently active
     *        (do not pass arguments, i.e. 'undefined' just get that value, note that some inputs might require you do it..)
     * @param {string} valueGlType GLSL type of the value
     * @return {string} valid GLSL oneliner (wihtout ';') for sampling the value, or invalid code (e.g. error message) to signal error
     */
    sample(value = undefined, valueGlType = 'void') {
        throw "FlexRenderer.UIControls.IControl::sample() must be implemented.";
    }

    /**
     * Parameters supported by this UI component, must contain at least
     *  - 'interactive' - type bool, enables and disables the control interactivity
     *  (by changing the content available when rendering html)
     *  - 'title' - type string, the control title
     *
     *  Additionally, for compatibility reasons, you should, if possible, define
     *  - 'default' - type any; the default value for the particular control
     * @return {{}} name: default value mapping
     */
    get supports() {
        throw "FlexRenderer.UIControls.IControl::supports must be implemented.";
    }

    /**
     * Type definitions for supports. Can return empty object. In case of missing
     * type definitions, the type is derived from the 'supports()' default value type.
     *
     * Each key must be an array of default values for the given key if applicable.
     * This is an _extension_ to the supports() and can be used only for keys that have more
     * than one default type applicable
     * @return {{}}
     */
    get supportsAll() {
        throw "FlexRenderer.UIControls.IControl::supportsAll must be implemented.";
    }

    /**
     * GLSL type of this control: what type is returned from this.sample(...) ?
     * @return {string}
     */
    get type() {
        throw "FlexRenderer.UIControls.IControl::type must be implemented.";
    }

    /**
     * Raw value sent to the GPU, note that not necessarily typeof raw() === type()
     * some controls might send whole arrays of data (raw) and do smart sampling such that type is only a number
     * @return {any}
     */
    get raw() {
        throw "FlexRenderer.UIControls.IControl::raw must be implemented.";
    }

    /**
     * Encoded value as used in the UI, e.g. a name of particular colormap, or array of string values of breaks...
     * @return {any}
     */
    get encoded() {
        throw "FlexRenderer.UIControls.IControl::encoded must be implemented.";
    }

    //////////////////////////////////////
    //////// COMMON API //////////////////
    //////////////////////////////////////

    /**
     * The control type component was registered with. Handled internally.
     * @return {*}
     */
    get uiControlType() {
        return this.constructor._type;
    }

    /**
     * Get current control parameters
     * the control should set the value as this._params = this.getParams(incomingParams);
     * @return {{}}
     */
    get params() {
        return this._params;
    }

    /**
     * Automatically overridden to return the name of the control it was registered with
     * @return {string}
     */
    getName() {
        return "IControl";
    }

    /**
     * Load a value from cache to support its caching - should be used on all values
     * that are available for the user to play around with and change using UI controls
     *
     * @param defaultValue value to return in case of no cached value, if undefined, it is fetched from supports()
     * @param paramName name of the parameter, must be equal to the name from 'supports' definition
     * @return {*} cached or default value
     */
    load(defaultValue, paramName = "") {
        return this.owner.loadProperty(this.name + (paramName === "default" ? "" : paramName),
            defaultValue === undefined ? this.supports[paramName] : defaultValue,
        );
    }

    /**
     * Store a value from cache to support its caching - should be used on all values
     * that are available for the user to play around with and change using UI controls
     *
     * @param value to store
     * @param paramName name of the parameter, must be equal to the name from 'supports' definition
     *  - default value can be empty string
     */
    store(value, paramName = "") {
        if (paramName === "default") {
            paramName = "";
        }
        this.owner.storeProperty(this.name + paramName, value);
    }

    /**
     * On parameter change register self
     * @param {string} event which event to fire on
     *  - events are with inputs the names of supported parameters (this.supports), separated by dot if nested
     *  - most controls support "default" event - change of default value
     *  - see specific control implementation to see what events are fired (Advanced Slider fires "breaks" and "mask" for instance)
     * @param {function} clbck(rawValue, encodedValue, context) call once change occurs, context is the control instance
     * TODO: use openseadragon event system directly here too
     */
    on(event, clbck) {
        this.__onchange[event] = clbck; //only one possible event -> rewrite?
    }

    /**
     * Clear events of the event type
     * @param {string} event type
     */
    off(event) {
        delete this.__onchange[event];
    }

    /**
     * Clear ALL events
     */
    clearEvents() {
        this.__onchange = {};
    }

    /**
     * Invoke changed value event
     *  -- should invoke every time a value changes !driven by USER!, and use unique or compatible
     *     event name (event 'value') so that shader knows what changed
     * @param event event to call
     * @param value decoded value of encodedValue
     * @param encodedValue value that was received from the UI input
     * @param context self reference to bind to the callback
     */
    changed(event, value, encodedValue, context) {
        if (typeof this.__onchange[event] === "function") {
            this.__onchange[event](value, encodedValue, context);
        }

        if (this._suppressVisualizationChanged) {
            return;
        }

        this.owner.backend.renderer.notifyVisualizationChanged({
            reason: "control-change",
            shaderId: this.owner.id,
            shaderType: this.owner.constructor.type(),
            controlName: this.name,
            controlVariableName: event,  // we use here event for names of the control vars like 'default', 'breaks'
            encodedValue: encodedValue,
            value: value
        });
    }

    /**
     * Create cache object to store this control's values.
     * @returns {object}
     */
    createCacheObject() {
        this._cache = {
            encodedValue: this.encoded,
            value: this.raw
        };
        return this._cache;
    }

    /**
     *
     * @param {object} cache object to serve as control's cache
     */
    loadCacheObject(cache) {
        this._cache = cache;
        this.set(cache.encodedValue);
    }
};


/**
 * Generic UI control implementations
 * used if:
 * {
 *     type: "CONTROL TYPE",
 *     ...
 * }
 *
 * The subclass constructor should get the owner reference, the name
 * of the input and the parametrization.
 *
 * Further parameters passed are dependent on the control type, see
 * @ FlexRenderer.UIControls
 *
 * @class FlexRenderer.UIControls.SimpleUIControl
 */
$.FlexRenderer.UIControls.SimpleUIControl = class extends $.FlexRenderer.UIControls.IControl {
    /**
     * Uses intristicComponent from UIControls._items that corresponds to type of this control.
     * @param {ShaderLayer} owner owner of the control (shaderLayer)
     * @param {string} name name of the control (eg. "opacity")
     * @param {string} id unique control's id, corresponds to it's DOM's element's id
     * @param {object} params
     * @param {object} intristicComponent control's object from UIControls._items, keyed with it's params.default.type?
     */
    constructor(owner, name, id, params, intristicComponent) {
        super(owner, name, id);
        this.component = intristicComponent;
        this._params = this.getParams(params);
        this._needsLoad = true;
    }

    /**
     * Set this.encodedValue to the default value defined in the intristicComponent.
     * Set this.value to the normalized value (from the encoded value) that will be sent to the GLSL.
     * Register "change" event handler to the control, if interactive.
     */
    init() {
        this.encodedValue = this.load(this.params.default);
        // nothing was stored in the cache so we got the default value from the load call => store the value in the cache
        if (this.encodedValue === this.params.default) {
            this.store(this.encodedValue);
        }

        /** Firstly decode encodedValue:
         *      for color it means that it is converted from string "#ffffff" to an array of three floats,
         *      for range it just parses the float on input.
         *  Secondly normalize the obtained value:
         *      for color it does nothing,
         *      for range it somehow gets it to the range <0, 1>;
         *          e.g.: with the range-min being 0 and range-max 100 and default value 40, it will set the min to 0, max to 100, and value to 0.4;
         *                  so that "distances" between the value and min and max remain the same.
         */
        this.value = this.component.normalize(this.component.decode(this.encodedValue), this.params);

        if (this.params.interactive) {
            const _this = this;
            let node = document.getElementById(this.id);
            if (node) {
                let updater = function(e) {
                    _this.set(e.target.value);
                    _this.owner.invalidate();
                };

                this._applyToNode(node, this.encodedValue);
                node.addEventListener('change', updater);
            } else {
                this._warnMissingNode("SimpleUIControl");
            }
        }
    }

    /**
     * Write the encoded value to the control's DOM element. Components whose input keeps its state
     * somewhere other than 'value' (checkbox 'checked', select needing a string) provide their own
     * 'applyToNode'; the rest use the plain assignment.
     */
    _applyToNode(node, encodedValue) {
        if (typeof this.component.applyToNode === "function") {
            this.component.applyToNode(node, encodedValue);
        } else {
            node.value = encodedValue;
        }
    }

    set(encodedValue) {
        this.encodedValue = encodedValue;
        this.value = this.component.normalize(this.component.decode(this.encodedValue), this.params);

        if (this.params.interactive) {
            let node = document.getElementById(this.id);
            if (node) {
                // no synthetic 'change' event dispatched here: the listener from init() would re-enter set()
                this._applyToNode(node, this.encodedValue);
            }
        }

        this.changed("default", this.value, this.encodedValue, this);
        this.store(this.encodedValue);
        this._needsLoad = true;
    }

    glDrawing(program, gl) {
        if (this._needsLoad) {
            // debugging purposes
            // console.debug('Setting', this.component.glUniformFunName(), 'corresponding to', this.webGLVariableName, 'to value', this.value);

            // A uniform upload is only valid while its own program is the current one. Under a
            // shared WebGL context CURRENT_PROGRAM is context-global, so a stale binding shows up
            // here as "location is not from the associated program" with no clue which control is
            // responsible. Debug-only: getParameter is a pipeline stall.
            if (this.owner && this.owner.backend && this.owner.backend.renderer &&
                    this.owner.backend.renderer.debug &&
                    gl.getParameter(gl.CURRENT_PROGRAM) !== program) {
                $.console.error(
                    `FlexRenderer: control '${this.webGLVariableName}' of shader '${this.owner.id}' ` +
                    `is uploading while a different program is bound. The uniform will be rejected.`
                );
            }

            gl[this.component.glUniformFunName()](this.glLocation, this.value);
            this._needsLoad = false;
        }
    }

    glLoaded(program, gl) {
        // debugging purposes
        // console.debug(`Setting control's glLocation to ${this.webGLVariableName}`);
        this.glLocation = gl.getUniformLocation(program, this.webGLVariableName);
        this._needsLoad = true;
    }

    toHtml(classes = "", css = "") {
        if (!this.params.interactive) {
            return "";
        }
        return this.component.html(this.id, this.params, classes, css);
    }

    define() {
        return `uniform ${this.component.glType} ${this.webGLVariableName};`;
    }

    sample(value = undefined, valueGlType = 'void') {
        if (!value || valueGlType !== 'float') {
            return this.webGLVariableName;
        }
        return this.component.sample(this.webGLVariableName, value);
    }

    get uiControlType() {
        return this.component["type"];
    }

    get supports() {
        return this.component.defaults();
    }

    get supportsAll() {
        return {};
    }

    get raw() {
        return this.value;
    }

    get encoded() {
        return this.encodedValue;
    }

    get type() {
        return this.component.glType;
    }
};

$.FlexRenderer.UIControls.SliderWithInput = class extends $.FlexRenderer.UIControls.IControl {
    static docs() {
        return {
            summary: "Compound float control composed of a range slider and number input.",
            description: "Creates two synchronized SimpleUIControl instances, one range and one number input, and uses the range control for GLSL definition and loading.",
            kind: "ui-control",
            parameters: [
                { name: "default", type: "number" },
                { name: "min", type: "number" },
                { name: "max", type: "number" },
                { name: "step", type: "number" },
                { name: "interactive", type: "boolean" },
                { name: "title", type: "string" }
            ],
            glType: "float"
        };
    }

    constructor(owner, name, webGLVariableName, params) {
        super(owner, name, webGLVariableName);
        this._c1 = new $.FlexRenderer.UIControls.SimpleUIControl(
            owner, name, webGLVariableName, params, $.FlexRenderer.UIControls.getUiElement('range'));
        params.title = "";
        this._c2 = new $.FlexRenderer.UIControls.SimpleUIControl(
            owner, name, webGLVariableName + "_2", params, $.FlexRenderer.UIControls.getUiElement('number'), "second-");
        this._c1._suppressVisualizationChanged = true;
        this._c2._suppressVisualizationChanged = true;
    }

    init() {
        const _this = this;
        this._c2._params = this._c1._params;
        this._c1.init();
        this._c2.init();
        this._c1.on("default", function(value, encoded, owner) {
            const c2 = document.getElementById(_this._c2.id);
            if (c2) {
                c2.value = encoded;
            }
            _this._c2.value = value;
            _this._c2.encodedValue = encoded;
            _this.changed("default", value, encoded, owner);
        }, true); //silently fail if registered
        this._c2.on("default", function(value, encoded, owner) {
            const c1 = document.getElementById(_this._c1.id);
            if (c1) {
                c1.value = encoded;
            }
            _this._c1.value = value;
            _this._c1.encodedValue = encoded;
            // Only C1 loads values to gpu, request change
            _this._c1._needsLoad = true;
            _this.changed("default", value, encoded, owner);
        }, true); //silently fail if registered
    }

    /**
     * Writing the range half is enough: its "default" handler registered in init()
     * mirrors the value into _c2 (and its DOM node) and raises the change event on this control.
     */
    set(encodedValue) {
        this._c1.set(encodedValue);
    }

    glDrawing(program, gl) {
        this._c1.glDrawing(program, gl);
    }

    glLoaded(program, gl) {
        this._c1.glLoaded(program, gl);
    }

    toHtml(classes = "", css = "") {
        if (!this._c1.params.interactive) {
            return "";
        }

        const titleHtml = this._c1.params.title
            ? `<span class="er-control__title er-control__title--slider-with-input">${this._c1.params.title}</span>`
            : "";

        const rangeHtml = $.FlexRenderer.UIControls.getUiElement("range").html(
            this._c1.id,
            { ...this._c1.params, title: "" },
            classes,
            `${css}width:100%;`
        );

        const numberHtml = $.FlexRenderer.UIControls.getUiElement("number").html(
            this._c2.id,
            { ...this._c2.params, title: "" },
            classes,
            css
        );

        return [
            `<div class="er-control er-control--slider-with-input" data-columns="${this.layoutColumns}">`,
            titleHtml,
            `<div class="er-control__body er-control__body--slider-with-input">`,
            rangeHtml,
            `</div>`,
            `<div class="er-control__aux er-control__aux--slider-with-input">`,
            numberHtml,
            `</div>`,
            `</div>`
        ].join("");
    }

    get layoutColumns() {
        return this._c1.params.title ? 3 : 2;
    }

    define() {
        return this._c1.define();
    }

    sample(ratio) {
        return this._c1.sample(ratio);
    }

    get supports() {
        return this._c1.supports;
    }

    get supportsAll() {
        return this._c1.supportsAll;
    }

    get params() {
        return this._c1.params;
    }

    get type() {
        return this._c1.type;
    }

    get raw() {
        return this._c1.raw;
    }

    get encoded() {
        return this._c1.encoded;
    }
};
$.FlexRenderer.UIControls.registerClass("range_input", $.FlexRenderer.UIControls.SliderWithInput);
})(OpenSeadragon);
